import { useEffect, useRef, type ReactNode } from 'react';

export type TextOverviewMode = 'auto' | 'always' | 'off';

/** Read-only overview of the editor's rendered lines; never touches draft content. */
export default function TextMinimap({ children, mode = 'auto' }: { children: ReactNode; mode?: TextOverviewMode }) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewportRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const body = bodyRef.current;
    const map = mapRef.current;
    const canvas = canvasRef.current;
    const viewport = viewportRef.current;
    const scroller = body?.querySelector<HTMLElement>('.cf-markdown-editor');
    const document = scroller?.querySelector<HTMLElement>('.ProseMirror');
    if (!body || !map || !canvas || !viewport || !scroller || !document) return;
    let frame = 0;
    const draw = () => {
      frame = 0;
      if (mode === 'off' || !document.textContent?.trim() || (mode === 'auto' && scroller.scrollHeight < scroller.clientHeight * 3)) {
        map.style.visibility = 'hidden';
        return;
      }
      const width = map.clientWidth;
      if (!width) return;
      const bounds = document.getBoundingClientRect();
      const lines: DOMRect[] = [];
      const walker = window.document.createTreeWalker(document, NodeFilter.SHOW_TEXT);
      const range = window.document.createRange();
      while (walker.nextNode()) {
        if (!walker.currentNode.textContent?.trim()) continue;
        range.selectNodeContents(walker.currentNode);
        lines.push(...range.getClientRects());
      }
      // Use actual text height, not the editor's empty minimum height.
      const contentHeight = lines.reduce((height, line) => Math.max(height, line.bottom - bounds.top), 1);
      const maxHeight = Math.min(300, body.clientHeight * 0.65);
      const scaleX = (width - 12) / Math.max(document.clientWidth, 1);
      const scaleY = Math.min(scaleX, Math.max(1, maxHeight - 12) / contentHeight);
      const height = Math.max(24, contentHeight * scaleY + 12);
      map.style.height = `${height}px`;
      const editorBounds = body.closest('.cf-editor')?.getBoundingClientRect();
      if (editorBounds) map.style.bottom = `${editorBounds.bottom - scroller.getBoundingClientRect().bottom}px`;
      map.style.visibility = lines.length ? 'visible' : 'hidden';
      const ratio = window.devicePixelRatio || 1;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.scale(ratio, ratio);
      context.fillStyle = getComputedStyle(document).color;
      context.globalAlpha = 0.32;
      for (const line of lines) {
        context.fillRect(6 + (line.left - bounds.left) * scaleX, 6 + (line.top - bounds.top) * scaleY, line.width * scaleX, Math.max(1, line.height * scaleY * 0.4));
      }
      viewport.hidden = scroller.scrollHeight <= scroller.clientHeight + 1;
      const viewHeight = Math.min(height - 12, scroller.clientHeight * scaleY);
      viewport.style.top = `${6 + Math.min(scroller.scrollTop, Math.max(0, contentHeight - scroller.clientHeight)) * scaleY}px`;
      viewport.style.height = `${viewHeight}px`;
      map.dataset.scale = String(scaleY);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(draw);
    };
    const resize = new ResizeObserver(schedule);
    resize.observe(body);
    resize.observe(document);
    resize.observe(map);
    const mutations = new MutationObserver(schedule);
    mutations.observe(document, { childList: true, characterData: true, subtree: true, attributes: true });
    scroller.addEventListener('scroll', schedule, { passive: true });
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      mutations.disconnect();
      scroller.removeEventListener('scroll', schedule);
    };
  }, [mode]);

  return <div className="cf-editor-body" ref={bodyRef}>
    <button type="button" className="cf-text-minimap" ref={mapRef} style={{ visibility: 'hidden' }} aria-label="Text overview: click to navigate. Use arrow keys to scroll." title="Text overview · click to navigate" onPointerDown={event => {
      event.preventDefault();
      const scroller = bodyRef.current?.querySelector<HTMLElement>('.cf-markdown-editor');
      const scale = Number(event.currentTarget.dataset.scale);
      if (scroller && scale > 0) scroller.scrollTop = (event.clientY - event.currentTarget.getBoundingClientRect().top - 6) / scale - scroller.clientHeight / 2;
    }} onKeyDown={event => {
      const scroller = bodyRef.current?.querySelector<HTMLElement>('.cf-markdown-editor');
      if (!scroller) return;
      const steps: Record<string, number> = { ArrowDown: 80, ArrowUp: -80, PageDown: scroller.clientHeight, PageUp: -scroller.clientHeight };
      if (event.key in steps || event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        scroller.scrollTop = event.key === 'Home' ? 0 : event.key === 'End' ? scroller.scrollHeight : scroller.scrollTop + steps[event.key];
      }
    }}><canvas ref={canvasRef} aria-hidden="true"/><span className="cf-minimap-viewport" ref={viewportRef} aria-hidden="true"/></button>
    {children}
  </div>;
}
